/**
 * useModerationCountdown hook
 *
 * Manages a countdown timer for the moderation session TTL.
 * Counts down from the configured `humanModerationAssignedTtlInSeconds`
 * and exposes state for the timer display and expiry warning dialog.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** Threshold (in seconds) at which the expiry warning is triggered */
const EXPIRY_WARNING_THRESHOLD_SECONDS = 60;

/** Format remaining seconds as "M:SS" */
function formatTime(totalSeconds: number): string {
  if (totalSeconds <= 0) return '0:00';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Return shape for `useModerationCountdown` */
export interface UseModerationCountdownReturn {
  /** Current remaining seconds */
  remainingSeconds: number;
  /** True when remaining time is ≤ 60 s and > 0 */
  isExpiringSoon: boolean;
  /** True when the countdown has reached zero */
  isExpired: boolean;
  /** Human-readable time remaining (e.g. "4:32") */
  formattedTime: string;
  /** Whether the expiry warning dialog should be shown */
  showWarning: boolean;
  /** Dismiss the expiry warning dialog */
  dismissWarning: () => void;
  /** Whether the session-expired dialog should be shown */
  showExpired: boolean;
  /** Dismiss the session-expired dialog */
  dismissExpired: () => void;
}

/**
 * Countdown timer for a moderation session's assigned-cases TTL.
 *
 * @param ttlInSeconds - Total time-to-live from the topic configuration
 * @param isActive     - Whether the session is active (cases are loaded)
 */
export function useModerationCountdown(ttlInSeconds: number, isActive: boolean): UseModerationCountdownReturn {
  const [remainingSeconds, setRemainingSeconds] = useState(ttlInSeconds);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [expiredDismissed, setExpiredDismissed] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isActive || ttlInSeconds <= 0) return;

    setRemainingSeconds(ttlInSeconds);
    setWarningDismissed(false);
    setExpiredDismissed(false);

    intervalRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 0) {
          if (intervalRef.current) clearInterval(intervalRef.current);

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [ttlInSeconds, isActive]);

  const isExpiringSoon = remainingSeconds <= EXPIRY_WARNING_THRESHOLD_SECONDS && remainingSeconds > 0;
  const isExpired = remainingSeconds <= 0;
  const formattedTime = formatTime(remainingSeconds);
  const showWarning = isExpiringSoon && !warningDismissed;
  const dismissWarning = useCallback(() => setWarningDismissed(true), []);
  const showExpired = isExpired && !expiredDismissed && ttlInSeconds > 0;
  const dismissExpired = useCallback(() => setExpiredDismissed(true), []);

  return { remainingSeconds, isExpiringSoon, isExpired, formattedTime, showWarning, dismissWarning, showExpired, dismissExpired };
}
